import React, { useState, useRef, useEffect } from 'react';
import { MapPin } from 'lucide-react';

// Common Indian cities and work modes
const LOCATION_SUGGESTIONS = [
  'Remote', 'Work From Home', 'Hybrid',
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai',
  'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Surat',
  'Lucknow', 'Kanpur', 'Nagpur', 'Indore', 'Thane',
  'Bhopal', 'Visakhapatnam', 'Pimpri-Chinchwad', 'Patna',
  'Vadodara', 'Ghaziabad', 'Ludhiana', 'Agra', 'Nashik',
  'Faridabad', 'Meerut', 'Rajkot', 'Varanasi', 'Srinagar',
  'Aurangabad', 'Dhanbad', 'Amritsar', 'Navi Mumbai',
  'Allahabad', 'Ranchi', 'Howrah', 'Coimbatore', 'Jabalpur',
  'Gwalior', 'Vijayawada', 'Jodhpur', 'Madurai', 'Raipur'
];

/**
 * Location Selector with Tag Support and Autocomplete
 * @param {String|Array} selectedLocation - Currently selected location(s)
 * @param {Function} onChange - Callback when location changes
 * @param {Boolean} multiple - Allow multiple locations (tag mode)
 * @param {Boolean} required - Whether location is required
 * @param {String} placeholder - Placeholder text
 */
const LocationSelector = ({ 
  selectedLocation = '', 
  onChange, 
  multiple = false,
  required = false,
  placeholder = 'Select city or work mode...'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Normalize selectedLocation to array for tag mode
  const selectedLocations = multiple 
    ? (Array.isArray(selectedLocation) ? selectedLocation : [selectedLocation].filter(Boolean))
    : [];

  // Sync inputValue with selectedLocation in single mode when not editing
  useEffect(() => {
    if (!multiple && !isEditing && selectedLocation) {
      setInputValue(selectedLocation);
    }
  }, [selectedLocation, multiple, isEditing]);

  // Filter suggestions based on input
  useEffect(() => {
    const searchValue = multiple ? inputValue : (isEditing ? inputValue : (inputValue || selectedLocation));
    
    if (searchValue && searchValue.trim()) {
      const filtered = LOCATION_SUGGESTIONS.filter(loc =>
        loc.toLowerCase().includes(searchValue.toLowerCase()) &&
        (!multiple || !selectedLocations.includes(loc))
      );

      setFilteredSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
    } else {
      setFilteredSuggestions(LOCATION_SUGGESTIONS.slice(0, 10));
    }
  }, [inputValue, selectedLocation, multiple, isEditing]); // Fixed: use selectedLocation instead of selectedLocations

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectLocation = (location) => {
    if (multiple) {
      if (!selectedLocations.includes(location)) {
        onChange([...selectedLocations, location]);
      }
      setInputValue('');
    } else {
      onChange(location);
      setInputValue(location);
      setIsEditing(false);
    }
    setShowSuggestions(false);
  };

  const handleRemoveLocation = (locationToRemove) => {
    onChange(selectedLocations.filter(loc => loc !== locationToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (multiple) {
        if (!selectedLocations.includes(trimmed)) {
          onChange([...selectedLocations, trimmed]);
        }
      } else {
        onChange(trimmed);
      }
      setInputValue('');
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && multiple && selectedLocations.length > 0) {
      handleRemoveLocation(selectedLocations[selectedLocations.length - 1]);
    }
  };

  return (
    <div className="w-full">
      {/* Selected Location Tags (Multiple Mode) */}
      {multiple && selectedLocations.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedLocations.map((location, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-medium"
            >
              <MapPin size={14} />
              {location}
              <button
                type="button"
                onClick={() => handleRemoveLocation(location)}
                className="hover:bg-blue-700 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${location}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!multiple) setIsEditing(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setShowSuggestions(true);
            if (!multiple && selectedLocation && !isEditing) {
              setIsEditing(true);
              setInputValue('');
            }
          }}
          onBlur={() => {
            // Delay to allow click on suggestion
            setTimeout(() => {
              if (!multiple && !inputValue && selectedLocation) {
                setInputValue(selectedLocation);
                setIsEditing(false);
              }
            }, 200);
          }}
          placeholder={placeholder}
          required={required && (multiple ? selectedLocations.length === 0 : !selectedLocation)}
          autoComplete="off"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
        />

        {/* Autocomplete Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectLocation(suggestion)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
              >
                <MapPin size={16} className="text-gray-400" />
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 mt-1">
        {multiple 
          ? 'Type to search, press Enter to add custom location' 
          : 'Start typing to see suggestions'}
      </p>
    </div>
  );
};

export default LocationSelector;
